// RaidChatSidebar.tsx - FIXED: Guild logos and Discord avatars
import React, { useState, useEffect, useRef, useCallback } from "react";

// ============================================================================
// CONFIGURATION
// ============================================================================

const BACKEND_URL = ""; // Your backend URL

// ============================================================================
// TYPES
// ============================================================================

export interface RaidChat {
  raid_id: string;
  raid_name: string;
  guild_id: string | null;
  guild_name: string | null;
  guild_tag: string | null;
  guild_logo_url: string | null;
  display_name: string;
  next_session_time: string;
  last_session_end: string;
  character_id: string;
  character_name: string;
  character_role: string;
  is_raid_leader: boolean;
  is_loot_master: boolean;
  unread_count: number;
}

interface ChatMessage {
  id: string;
  content: string;
  is_system: boolean;
  created_at: string;
  user_id: string;
  character_id: string;
  user_display_name: string;
  user_discord_id: string;
  character_name: string;
  character_class: string;
  is_raid_leader: boolean;
  is_loot_master: boolean;
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

// WoW Class Colors (Blizzard official colors)
const WOW_CLASS_COLORS: Record<string, { from: string; to: string }> = {
  'warrior': { from: '#C79C6E', to: '#A67D52' },
  'paladin': { from: '#F58CBA', to: '#D16FA0' },
  'hunter': { from: '#ABD473', to: '#8FB85E' },
  'rogue': { from: '#FFF569', to: '#E0D855' },
  'priest': { from: '#FFFFFF', to: '#E0E0E0' },
  'death knight': { from: '#C41F3B', to: '#9E1830' },
  'shaman': { from: '#0070DE', to: '#0059B3' },
  'mage': { from: '#69CCF0', to: '#54A6C7' },
  'warlock': { from: '#9482C9', to: '#7566A3' },
  'monk': { from: '#00FF96', to: '#00CC78' },
  'druid': { from: '#FF7D0A', to: '#CC6408' },
  'demon hunter': { from: '#A330C9', to: '#8226A1' },
};

// Get class color gradient
function getClassColor(className: string): { from: string; to: string } {
  const key = (className || '').toLowerCase();
  return WOW_CLASS_COLORS[key] || { from: '#6366F1', to: '#4F46E5' }; // Default: indigo
}

// Get full URL for guild logo
function getGuildLogoUrl(logoPath: string | null): string | null {
  if (!logoPath) return null;
  if (logoPath.startsWith('http')) return logoPath;
  return `${BACKEND_URL}${logoPath}`;
}

// Get Discord avatar URL
function getDiscordAvatarUrl(discordId: string): string {
  // Discord default avatars (colorful ones)
  const defaultAvatar = parseInt(discordId) % 5;
  return `https://cdn.discordapp.com/embed/avatars/${defaultAvatar}.png`;
}

// ============================================================================
// MAIN COMPONENT  
// ============================================================================

export default function RaidChatSidebar() {
  const [raids, setRaids] = useState<RaidChat[]>([]);
  const [expandedRaidId, setExpandedRaidId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  // Fetch user's raid chats
  const fetchRaids = useCallback(() => {
    fetch(`${BACKEND_URL}/raid-chat/my-raids`, { credentials: "include" })
      .then((r) => r.json())
      .then((data) => {
        setRaids(data);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Failed to load raid chats:", err);
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    fetchRaids();
  }, []);

  const handleBubbleClick = (raidId: string) => {
    setExpandedRaidId(expandedRaidId === raidId ? null : raidId);
  };

  const handleClose = () => {
    setExpandedRaidId(null);
    // Refresh raids when closing panel
    fetchRaids();
  };

  if (loading) return null;
  if (raids.length === 0) return null;

  return (
    <>
      {/* Raid Bubbles */}
      <div className="space-y-3">
        {raids.map((raid) => (
          <RaidBubble
            key={raid.raid_id}
            raid={raid}
            isExpanded={expandedRaidId === raid.raid_id}
            onClick={() => handleBubbleClick(raid.raid_id)}
          />
        ))}
      </div>

      {/* Chat Panel */}
      {expandedRaidId && raids.find((r) => r.raid_id === expandedRaidId) && (
        <RaidChatPanel
          raidId={expandedRaidId}
          raid={raids.find((r) => r.raid_id === expandedRaidId)!}
          onClose={handleClose}
        />
      )}
    </>
  );
}

// ============================================================================
// RAID BUBBLE COMPONENT
// ============================================================================

function RaidBubble({
  raid,
  isExpanded,
  onClick,
}: {
  raid: RaidChat;
  isExpanded: boolean;
  onClick: () => void;
}) {
  const [isHovered, setIsHovered] = useState(false);
  const [logoError, setLogoError] = useState(false);

  const logoUrl = getGuildLogoUrl(raid.guild_logo_url);

  // Format time
  const sessionDate = new Date(raid.next_session_time);
  const now = new Date();
  const isToday = sessionDate.toDateString() === now.toDateString();
  const isTomorrow = sessionDate.toDateString() === new Date(now.getTime() + 86400000).toDateString();

  const timeLabel = isToday
    ? `Today ${sessionDate.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`
    : isTomorrow
    ? `Tomorrow ${sessionDate.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`
    : sessionDate.toLocaleDateString([], { month: "short", day: "numeric" });

  return (
    <>
      <div className="relative" style={{ zIndex: 99999 }}>
        <button
          onClick={onClick}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          className={`
            relative group w-9 h-9 rounded-full transition-all duration-300 overflow-hidden
            ${
              isExpanded
                ? "bg-cyan-500/30 ring-2 ring-cyan-400/60 scale-110 shadow-lg shadow-cyan-500/20"
                : "bg-white/5 ring-1 ring-white/10 hover:bg-white/10 hover:ring-white/20 hover:shadow-md"
            }
          `}
        >
          {/* Guild Logo or Fallback */}
          {logoUrl && !logoError ? (
            <img
              src={logoUrl}
              alt={raid.display_name}
              className="w-full h-full object-cover"
              onError={() => setLogoError(true)}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-xs font-bold text-white/70 bg-gradient-to-br from-white/10 to-white/5">
              {raid.guild_tag || (raid.guild_name ? raid.guild_name.substring(0, 2).toUpperCase() : raid.display_name.substring(0, 2).toUpperCase())}
            </div>
          )}

          {/* Unread Badge */}
          {raid.unread_count > 0 && (
            <div className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-gradient-to-br from-red-500 to-red-600 ring-2 ring-[#09090b] flex items-center justify-center shadow-lg shadow-red-500/30 animate-pulse">
              <span className="text-[9px] font-bold text-white">
                {raid.unread_count > 99 ? "99+" : raid.unread_count}
              </span>
            </div>
          )}

          {/* Pulse animation for raids starting soon */}
          {new Date(raid.next_session_time).getTime() - now.getTime() < 3600000 && (
            <div className="absolute inset-0 rounded-full bg-cyan-400/20 animate-ping" />
          )}
        </button>

        {/* Tooltip on Hover */}
        {isHovered && !isExpanded && (
          <div className="absolute right-full mr-3 top-0 z-[99999] pointer-events-none animate-in fade-in slide-in-from-right-2 duration-200">
            <div className="rounded-lg bg-gradient-to-br from-black/95 to-black/90 border border-cyan-500/40 px-4 py-3 shadow-2xl backdrop-blur-sm min-w-[220px]">
              <div className="text-sm font-bold text-cyan-100 mb-1">{raid.raid_name}</div>
              <div className="text-[11px] text-white/70 mb-2">
                {raid.guild_tag && <span className="text-cyan-300/80">&lt;{raid.guild_tag}&gt;</span>} {raid.display_name}
              </div>
              <div className="flex items-center gap-2 text-[10px] text-white/50 mb-1">
                <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                {timeLabel}
              </div>
              <div className="flex items-center gap-2 text-[10px] text-white/40">
                <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
                {raid.character_name} · {raid.character_role}
              </div>
              
              {/* Arrow - Now aligned to the top of the bubble (roughly 18px down) */}
              <div className="absolute left-full top-[14px] -ml-1 h-2 w-2 rotate-45 bg-black/90 border-r border-t border-cyan-500/40" />
            </div>
          </div>
        )}
      </div>
    </>
  );
}

// ============================================================================
// CHAT PANEL COMPONENT
// ============================================================================

export function RaidChatPanel({
  raidId,
  raid,
  onClose,
}: {
  raidId: string;
  raid: RaidChat;
  onClose: () => void;
}) {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [newMessage, setNewMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [wsConnected, setWsConnected] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Scroll to bottom
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  // Fetch messages
  useEffect(() => {
    fetch(`${BACKEND_URL}/raid-chat/${raidId}/messages?limit=100`, {
      credentials: "include",
    })
      .then((r) => r.json())
      .then((data) => {
        setMessages(data);
        setLoading(false);
        setTimeout(scrollToBottom, 100);
      })
      .catch(console.error);

    // Mark as read
    fetch(`${BACKEND_URL}/raid-chat/${raidId}/read`, {
      method: "PATCH",
      credentials: "include",
    });
  }, [raidId]);

  // WebSocket connection with auto-reconnect
  useEffect(() => {
    let isSubscribed = false;

    const connectWebSocket = () => {
      const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
      const ws = new WebSocket(`${protocol}//${window.location.host}`);
      wsRef.current = ws;

      ws.onopen = () => {
        console.log("✅ WebSocket connected");
        setWsConnected(true);
        ws.send(JSON.stringify({ type: "subscribe_raid", data: { raidId } }));
        isSubscribed = true;
      };

      ws.onmessage = (event) => {
        const msg = JSON.parse(event.data);
        
        if (msg.type === "subscribed") {
          console.log("✅ Subscribed to raid:", raidId);
        }

        if (msg.type === "new_message") {
          console.log("📨 New message received:", msg.data);
          setMessages((prev) => [...prev, msg.data]);
          setTimeout(scrollToBottom, 100);
        }

        if (msg.type === "message_deleted") {
          setMessages((prev) => prev.filter((m) => m.id !== msg.data.messageId));
        }
      };

      ws.onerror = (error) => {
        console.error("❌ WebSocket error:", error);
        setWsConnected(false);
      };

      ws.onclose = () => {
        console.log("🔌 WebSocket disconnected");
        setWsConnected(false);
        isSubscribed = false;

        // Auto-reconnect after 2 seconds
        reconnectTimeoutRef.current = setTimeout(() => {
          console.log("🔄 Reconnecting WebSocket...");
          connectWebSocket();
        }, 2000);
      };
    };

    connectWebSocket();

    return () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (wsRef.current?.readyState === 1 && isSubscribed) {
        wsRef.current.send(JSON.stringify({ type: "unsubscribe_raid", data: { raidId } }));
      }
      wsRef.current?.close();
    };
  }, [raidId]);

  // Send message
  const handleSend = async () => {
    if (!newMessage.trim() || sending) return;

    setSending(true);
    try {
      const response = await fetch(`${BACKEND_URL}/raid-chat/${raidId}/messages`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ content: newMessage }),
      });

      if (response.ok) {
        setNewMessage("");
        // Message will appear via WebSocket, but if WS is disconnected, add manually
        if (!wsConnected) {
          const data = await response.json();
          setMessages((prev) => [...prev, data]);
          setTimeout(scrollToBottom, 100);
        }
      }
    } catch (error) {
      console.error("Failed to send message:", error);
    } finally {
      setSending(false);
    }
  };

  // Format timestamp
  const formatTime = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();
    
    if (isToday) {
      return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    }
    return date.toLocaleDateString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
  };

  return (
      <div className="fixed right-20 top-20 bottom-4 w-96 z-[99999] animate-in slide-in-from-right-5 fade-in duration-300">
        <div className="h-full flex flex-col rounded-xl border border-white/10 bg-[#09090b]/95 backdrop-blur-xl shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-white/10 bg-gradient-to-r from-cyan-500/10 to-purple-500/10 relative">
          <div className="flex-1 min-w-0">
            <div className="font-bold text-sm text-white truncate">{raid.raid_name}</div>
            <div className="text-xs text-white/60 truncate">
              {raid.guild_tag && <span className="text-cyan-300/70">&lt;{raid.guild_tag}&gt;</span>} {raid.display_name}
            </div>
          </div>
          
          {/* Connection indicator */}
          <div className="mx-2 flex items-center gap-1.5">
            <div className={`w-1.5 h-1.5 rounded-full ${wsConnected ? 'bg-green-400 shadow-lg shadow-green-400/50' : 'bg-red-400'}`} />
          </div>

          <button
            onClick={onClose}
            className="w-7 h-7 rounded-md hover:bg-white/10 flex items-center justify-center transition-colors group"
          >
            <svg className="w-4 h-4 text-white/60 group-hover:text-white/90 transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Raid Info Banner */}
        <div className="px-4 py-2.5 bg-black/40 border-b border-white/5 text-xs">
          <div className="flex items-center gap-2 text-white/60">
            <svg className="w-3.5 h-3.5 text-cyan-400/70" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span className="text-white/70">
              {new Date(raid.next_session_time).toLocaleDateString([], { 
                month: "short", 
                day: "numeric", 
                hour: "2-digit", 
                minute: "2-digit" 
              })}
            </span>
            <span className="text-white/40">·</span>
            <span className="text-cyan-400 font-medium">{raid.character_name}</span>
            <span className="text-white/40 text-[10px]">{raid.character_role}</span>
            {raid.is_raid_leader && (
              <span className="px-1.5 py-0.5 rounded bg-amber-500/20 border border-amber-500/30 text-amber-300 text-[9px] font-bold">RL</span>
            )}
            {raid.is_loot_master && (
              <span className="px-1.5 py-0.5 rounded bg-purple-500/20 border border-purple-500/30 text-purple-300 text-[9px] font-bold">LM</span>
            )}
          </div>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3 scrollbar-thin scrollbar-thumb-white/10 scrollbar-track-transparent">
          {loading ? (
            <div className="flex items-center justify-center h-full text-white/40 text-sm">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-cyan-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                <div className="w-2 h-2 bg-cyan-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                <div className="w-2 h-2 bg-cyan-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
              </div>
            </div>
          ) : messages.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center space-y-3">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-cyan-500/20 to-purple-500/20 flex items-center justify-center">
                <svg className="w-8 h-8 text-cyan-400/50" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
              </div>
              <div>
                <div className="text-white/60 text-sm font-medium mb-1">No messages yet</div>
                <div className="text-white/40 text-xs">Say hi to the raid! 👋</div>
              </div>
            </div>
          ) : (
            messages.map((msg) => <MessageBubble key={msg.id} message={msg} formatTime={formatTime} />)
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <div className="px-4 py-3 border-t border-white/10 bg-black/40 backdrop-blur-sm">
          <div className="flex gap-2">
            <input
              type="text"
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && handleSend()}
              placeholder="Type a message..."
              maxLength={2000}
              disabled={sending}
              className="flex-1 rounded-lg border border-white/10 bg-white/5 px-3 py-2.5 text-sm text-white placeholder:text-white/40 outline-none focus:border-cyan-500/50 focus:bg-white/10 focus:ring-2 focus:ring-cyan-500/20 transition-all disabled:opacity-50"
            />
            <button
              onClick={handleSend}
              disabled={!newMessage.trim() || sending}
              className="px-4 py-2.5 rounded-lg bg-gradient-to-r from-cyan-500/20 to-cyan-600/20 border border-cyan-500/40 text-cyan-200 text-sm font-bold hover:from-cyan-500/30 hover:to-cyan-600/30 hover:border-cyan-400/50 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-lg shadow-cyan-500/10 hover:shadow-cyan-500/20"
            >
              {sending ? "..." : "Send"}
            </button>
          </div>
          <div className="mt-1.5 text-[10px] text-white/30 text-right">
            {newMessage.length}/2000
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// MESSAGE BUBBLE
// ============================================================================

function MessageBubble({ 
  message, 
  formatTime 
}: { 
  message: ChatMessage; 
  formatTime: (timestamp: string) => string;
}) {
  // Convert URLs to clickable links
  const renderContent = (text: string) => {
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    const parts = text.split(urlRegex);
    
    return parts.map((part, i) => {
      if (part.match(urlRegex)) {
        return (
          <a
            key={i}
            href={part}
            target="_blank"
            rel="noopener noreferrer"
            className="text-cyan-400 underline decoration-cyan-400/50 hover:text-cyan-300 hover:decoration-cyan-300 transition-colors font-medium break-all cursor-pointer hover:bg-cyan-500/10 px-0.5 rounded"
            onClick={(e) => e.stopPropagation()}
          >
            {part}
          </a>
        );
      }
      return <span key={i}>{part}</span>;
    });
  };

  if (message.is_system) {
    return (
      <div className="flex justify-center py-2">
        <div className="px-3 py-1.5 rounded-full bg-white/5 border border-white/5 text-xs text-white/50">
          {message.content}
        </div>
      </div>
    );
  }

  // Get first letter and class color
  const firstLetter = (message.character_name || message.user_display_name || '?')[0].toUpperCase();
  const classColor = getClassColor(message.character_class);

  return (
    <div className="flex gap-2.5 group hover:bg-white/[0.02] -mx-2 px-2 py-1.5 rounded-lg transition-colors">
      {/* Avatar - First letter with WoW class color */}
      <div 
        className="w-9 h-9 rounded-full ring-1 ring-white/20 flex-shrink-0 flex items-center justify-center text-sm font-bold text-white shadow-inner"
        style={{ background: `linear-gradient(135deg, ${classColor.from}, ${classColor.to})` }}
      >
        {firstLetter}
      </div>

      <div className="flex-1 min-w-0">
        {/* Header */}
        <div className="flex items-center gap-2 mb-1">
          <span className="font-bold text-xs text-white">{message.character_name}</span>
          
          {/* Role badges */}
          {message.is_raid_leader && (
            <span className="px-1.5 py-0.5 rounded bg-amber-500/20 border border-amber-500/30 text-amber-300 text-[9px] font-bold">RL</span>
          )}
          {message.is_loot_master && (
            <span className="px-1.5 py-0.5 rounded bg-purple-500/20 border border-purple-500/30 text-purple-300 text-[9px] font-bold">LM</span>
          )}
          
          <span className="text-[10px] text-white/40">{formatTime(message.created_at)}</span>
        </div>

        {/* Message content */}
        <div className="text-sm text-white/90 break-words leading-relaxed">
          {renderContent(message.content)}
        </div>
      </div>
    </div>
  );
}
